<template>
    <td
        :rowspan="rowSpan"
        :class="type + (useLineTextEllipsis ? ' ellipsis' : '') + (cssClass !== '' ? ' ' + cssClass : '') + (validationHint ? ' validationHint' : '')"
    >
        <toggler :initialEditMode="initialEditMode" :readonly="readonly">
            <template #activator="{ onClick }">
                <GroupedTableCell
                    v-if="groupValuesMap"
                    :key="'group_' + groupedValuesKey"
                    :groups="groupedValues"
                    @onEdit="onClick()"
                />
                <div v-else-if="useLineTextEllipsis">
                    <div
                        :key="'element_' + element"
                        @click="onClick"
                        class="ellipsis"
                        v-for="element in cellContent"
                    >{{element}}</div>
                </div>
                <div v-else @click="onClick">{{cellContent || $t('-- please select --')}}</div>
            </template>
            <template #deactivator="{ onHide }">
                <v-autocomplete
                    v-model="selectValue"
                    :key="'select_' + type"
                    :items="allValues"
                    item-value="id"
                    item-text="name"
                    item-color="var(--sc-blue-dark)"
                    :multiple="multiSelect"
                    :menu-props="{maxHeight: 800}"
                    v-on:blur="onHide"
                    @focusout="changeSelection"
                    :readonly="readonly"
            >
                <template v-slot:prepend-item>
                    <v-list-item
                        v-if="multiSelect"
                        @click="selectAllToggle"
                    >
                        <v-list-item-action>
                            <v-icon color="var(--sc-blue-dark)">{{ icon }}</v-icon>
                        </v-list-item-action>
                        <v-list-item-content>
                            <v-list-item-title><strong>Select All</strong></v-list-item-title>
                        </v-list-item-content>

                    </v-list-item>
                    <v-divider
                        v-if="multiSelect"
                        slot="prepend-item"
                        class="mt-2"
                    />
                </template>
            </v-autocomplete>
            </template>
        </toggler>
        <div v-if="!readonly && showAddRow" class="addRowPlus" :type="type" @click="addRow"/>
    </td>
</template>

<script>
    import Toggler from "../Toggler";
    import GroupedTableCell from "../Table/GroupedTableCell.vue";
    export default {
        name: "CedexDependenciesSelectTableCell",
        components: {GroupedTableCell, Toggler},
        props: {
            type:            String,
            allValuesObject: Object,
            allValuesArray:  Array,
            value: {
                type: String,
                validator: function(value) {
                    return (typeof value === 'string') || value === null;
                }
            },

            codeTexts: {
                type:    [Boolean,String],
                default: false
            },
            groupValuesMap: {
                type:    Object,
                validator: function(value) {
                    return (typeof value === 'object') || value === null;
                },
                default: null
            },
            cssClass: {
                type: String,
                default: ''
            },
            multiSelect: {
                type:    Boolean,
                default: false
            },
            rowSpan: {
                type:    Number,
                default: 1
            },
            useLineTextEllipsis: {
                type:    Boolean,
                default: false
            },
            showAllValuesAsValues: {
                type:    Boolean,
                default: false
            },
            showAddRow: {
                type:    Boolean,
                default: false
            },
            initialEditMode: {
                type:    Boolean,
                default: false
            },
            addEmptyOption: {
                type:    Boolean,
                default: false
            },
            emptyOptionText: {
                type:    String,
                default: ''
            },
            readonly: {
                type:    Boolean,
                default: false,
            },
            validationHint: {
                type:    Boolean,
                default: false,
            },
        },
        data() {
            return {
                currentValue: this.value,
                lineBreak: '\n',
                specialLineBreaks: {
                    inspectionCriteria: ', ',
                    repairArea: ', '
                },
                onHide: false
            }
        },
        computed: {
            valueCount() {
                return Object.keys(this.allValuesObject).length;
            },
            selectValue: {
                // getter
                get: function () {
                    if (this.multiSelect) {
                        if (
                            this.currentValue === null
                            || this.currentValue === ''
                        ) {
                            // null => all values
                            // empty string => nothing selected
                            return this.currentValue;
                        } else {
                            return this.currentValue.split(',');
                        }
                    } else {
                        return this.currentValue;
                    }
                },
                // setter
                set: function (newValue) {
                    if (this.multiSelect) {
                        newValue = newValue.filter(function (el) {return el !== '';});
                        if (newValue.length === this.valueCount) {
                            // null => all values
                            this.currentValue = null
                        } else {
                            this.currentValue = newValue.join(',');
                        }
                    } else {
                        this.currentValue = newValue;
                    }
                }
            },
            /* Returns an object of value array grouped by property groupValuesMap */
            groupedValues() {
                let values;
                if (this.currentValue === null) {
                    values = Object.keys(this.allValuesObject);
                } else if (this.currentValue === '') {
                    values = [];
                } else {
                    values = this.currentValue.split(',');
                }

                values = this.allValuesArray
                    .map((value) => value.id) // parse object to ids/codes
                    .filter((value) => value !== undefined) // filter empty ones (group dummies)
                    .filter((value) => values.includes(value)) // filter empty ones (group dummies)
                ;

                let groupedValues = {};
                let groupedValueCount = {};
                let groupName;
                for (let value in this.allValuesObject) {
                    if (this.type === 'locationCode') {
                        groupName = this.groupValuesMap[value.substring(0,1)];
                    } else {
                        groupName = this.groupValuesMap[value];
                    }
                    if (typeof groupedValueCount[groupName] === 'undefined') {
                        groupedValueCount[groupName] = 0;
                    }
                    groupedValueCount[groupName]++;
                }
                for (let valueIndex in values) {
                    let value     = values[valueIndex];
                    if (this.type === 'locationCode') {
                        groupName = this.groupValuesMap[value.substring(0,1)];
                    } else {
                        groupName = this.groupValuesMap[value];
                    }
                    if (typeof groupedValues[groupName] === 'undefined') {
                        groupedValues[groupName] = [];
                    }
                    groupedValues[groupName].push(this.getElementText(value))
                }
                groupedValues.__counts = groupedValueCount;

                return groupedValues;
            },
            groupedValuesKey() {
                let key;
                if (this.currentValue === null) {
                    key = Object.keys(this.allValuesObject).join(',');
                } else {
                    key = this.currentValue;
                }
                return key;
            },
            icon() {
                if (
                    this.selectValue === null
                    || this.selectValue.length === this.valueCount
                ) {
                    return 'mdi-close-box'
                } else if (
                    this.selectValue.length > 0
                    && this.selectValue.length !== this.valueCount
                ) {
                    return 'mdi-minus-box';
                } else {
                    return 'mdi-checkbox-blank-outline'
                }
            },
            /* Returns the cell content depending column type, showAllValuesAsValues, useLineTextEllipsis and special line break  */
            cellContent() {

                let skipJoin = this.useLineTextEllipsis;

                let values;
                if (this.currentValue === null) {
                    if (this.showAllValuesAsValues) {
                        values = Object.keys(this.allValuesObject);
                    } else {
                        if (skipJoin) {
                            return ['-- all Values --'];
                        }
                        return '-- all Values --';
                    }
                } else if (this.currentValue === '') {
                    if (skipJoin) {
                        return ['-- please select --'];
                    }
                    return '-- please select --'
                } else {
                    values = this.currentValue.split(',');
                }

                let returnValues = [];

                for (let index in values) {
                    returnValues.push(this.getElementText(values[index]));
                }

                if (returnValues.length === 0) {
                    returnValues = ['-- please select --'];
                }
                if (skipJoin) {
                    return returnValues;
                }

                return returnValues.join(this.specialLineBreaks[this.type] || this.lineBreak);
            },
            allValues() {
                let allValues = this.allValuesArray;
                if (this.emptyOptionText !== '') {
                    allValues.splice(0, 0, {
                        id:       '',
                        name:     this.emptyOptionText,
                        disabled: !this.addEmptyOption,
                    })
                }

                return allValues;
            }
        },
        methods: {
            addRow() {
                if (this.readonly) {
                    return;
                }
                this.$emit('addRow');
            },
            changeSelection() {
                if (this.readonly) {
                    return;
                }
                this.$emit('changeSelection', this.selectValue);
            },
            selectAllToggle() {
                if (this.readonly) {
                    return;
                }
                if (
                    this.selectValue === null
                    || this.selectValue.length === this.valueCount
                ) {
                    this.currentValue = null;
                } else {
                    this.currentValue = Object.keys(this.allValuesObject).join(',');
                }
                this.$emit('changeSelection', this.selectValue);
            },
            /* Returns the text of value depending on column type, codeTexts parameter and existence of allValuesObject */
            getElementText(value) {
                let text;
                if (this.allValuesObject) {
                    if (this.codeTexts === true) {
                        text = this.allValuesObject[value];
                        if (
                            this.type !== 'serviceType'
                            && this.type !== 'surveyType'
                            && this.type !== 'repairArea'
                            && this.type !== 'inspectionCriteria'
                        ) {
                            text = value + ' - ' + text;
                        }
                    } else if (this.codeTexts === 'label') {
                        text = this.allValuesObject[value];
                    } else {
                        if (
                            this.type !== 'serviceType'
                            && this.type !== 'surveyType'
                            && this.type !== 'repairArea'
                            && this.type !== 'inspectionCriteria'
                        ) {
                            text = value;
                        } else {
                            text = this.allValuesObject[value];
                        }
                    }
                } else {
                    text = value;
                }

                return text;
            }
        }
    }
</script>

<style scoped>
    div.editor-box table.vcs-table td.ellipsis {
        white-space: pre-line;
    }
    div.editor-box table.vcs-table td.ellipsis div.ellipsis {
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
        white-space: nowrap;
    }
</style>